import { z } from 'zod';
import { counterpartyPartyTypeSchema } from './reference-books';
/**
 * Zod-схемы для поручений (Order) — ПП РФ № 1317 п.3 е).
 *
 * Order — это поручение клиента до котировки/акцепта. После акцепта котировки
 * порождает Contract (этап 5). Сейчас на этапе 4 закрываем full lifecycle Order'а
 * со статусами DRAFT → SUBMITTED → ... → COMPLETED.
 */
// =====================================================================
// Enums (синхронизированы с Prisma)
// =====================================================================
export const transportModeSchema = z.enum([
    'AUTO',
    'RAIL',
    'SEA',
    'INLAND_WATER',
    'AIR',
    'MULTIMODAL',
    'ANY',
]);
export const orderDirectionSchema = z.enum(['DOMESTIC', 'EXPORT', 'IMPORT', 'TRANSIT']);
/** Россия по названию или коду страны (свободный ввод в точках маршрута). */
export function isRussia(country) {
    const n = (country ?? '').trim().toLowerCase();
    return n === 'россия' || n === 'рф' || n === 'russia' || n === 'ru' || n === 'russian federation';
}
/**
 * Направление перевозки по странам отправления и назначения — оно из них
 * следует однозначно, поэтому в форме не выбирается, а показывается.
 *
 * РФ → РФ — внутренняя, РФ → заграница — экспортная, заграница → РФ —
 * импортная, заграница → заграница — транзитная. Пока страна хотя бы с одной
 * стороны не указана, направление не определено (null).
 */
export function deriveOrderDirection(fromCountry, toCountry) {
    if (!fromCountry?.trim() || !toCountry?.trim())
        return null;
    const fromRu = isRussia(fromCountry);
    const toRu = isRussia(toCountry);
    if (fromRu && toRu)
        return 'DOMESTIC';
    if (fromRu)
        return 'EXPORT';
    if (toRu)
        return 'IMPORT';
    return 'TRANSIT';
}
export const orderStatusSchema = z.enum([
    'DRAFT',
    'SUBMITTED',
    'QUOTED',
    'ACCEPTED',
    'REJECTED',
    'CANCELLED',
    'IN_PROGRESS',
    'COMPLETED',
]);
export const routePointKindSchema = z.enum(['PICKUP', 'DROPOFF']);
/** Кого уведомить о прибытии груза (формат ФНС: СвУведСторон). */
export const notifyPartySchema = z.enum(['CONSIGNEE', 'SHIPPER', 'CLIENT']);
// =====================================================================
// RoutePoint
// =====================================================================
export const routePointInputSchema = z.object({
    kind: routePointKindSchema,
    sequence: z.number().int().min(1),
    /** Страна точки маршрута (опц.). */
    country: z.string().max(100).optional(),
    address: z.string().max(500),
    counterpartyName: z.string().max(500).optional(),
    /** Снимок реквизитов стороны (для электронного титула ИдСв). */
    counterpartyType: counterpartyPartyTypeSchema.optional(),
    counterpartyInn: z.string().max(20).optional(),
    counterpartyKpp: z.string().max(20).optional(),
    counterpartyOgrn: z.string().max(20).optional(),
    contactName: z.string().max(200).optional(),
    contactPhone: z.string().max(50).optional(),
    /** ISO date или datetime — фронт оперирует строкой, сервер парсит. */
    date: z.string().optional(),
    /** Окно «9:00–12:00» в свободной форме. */
    timeWindow: z.string().max(100).optional(),
    loadingType: z.string().max(100).optional(),
    notes: z.string().max(2000).optional(),
});
export const routePointDtoSchema = routePointInputSchema.extend({
    id: z.string(),
    orderId: z.string(),
    date: z.string().nullable().optional(),
});
// =====================================================================
// OrderRouteSegment — плечи маршрута
// =====================================================================
/** Чем является пункт стыковки плеч. */
export const routeSegmentPointKindSchema = z.enum([
    'SHIPPER',
    'CONSIGNEE',
    'STATION',
    'PORT',
    'AIRPORT',
    'TERMINAL',
    'CUSTOMS',
    'OTHER',
]);
/**
 * Плечо маршрута — участок под одним видом транспорта. Заполняется опционально
 * (клиентом или экспедитором), осмысленно при нескольких видах транспорта.
 * Наименования станций/портов — свободный текст (справочника станций пока нет).
 */
export const orderRouteSegmentInputSchema = z.object({
    /** id существующего плеча (при правке) — для фронта; сервер пересоздаёт набор. */
    id: z.string().optional(),
    sequence: z.number().int().min(1),
    transportMode: transportModeSchema,
    fromKind: routeSegmentPointKindSchema,
    fromName: z.string().max(500),
    fromCountry: z.string().max(100).optional(),
    fromCountryCode: z.string().max(10).optional(),
    toKind: routeSegmentPointKindSchema,
    toName: z.string().max(500),
    toCountry: z.string().max(100).optional(),
    toCountryCode: z.string().max(10).optional(),
    /** ISO date или datetime — фронт оперирует строкой, сервер парсит. */
    dateFrom: z.string().optional(),
    dateTo: z.string().optional(),
    carrierName: z.string().max(300).optional(),
    comment: z.string().max(2000).optional(),
});
export const orderRouteSegmentDtoSchema = orderRouteSegmentInputSchema.extend({
    id: z.string(),
    orderId: z.string(),
    dateFrom: z.string().nullable().optional(),
    dateTo: z.string().nullable().optional(),
    /** Номера уже выпущенных на плечо документов (ЭТрН / поручение ФНС). */
    documentNumbers: z.array(z.string()).default([]),
});
/** Тип пункта стыковки, характерный для вида транспорта. */
const MODE_POINT_KIND = {
    RAIL: 'STATION',
    SEA: 'PORT',
    INLAND_WATER: 'PORT',
    AIR: 'AIRPORT',
    AUTO: 'TERMINAL',
};
/** Магистральные виды — те, что едут «между узлами», а не «от двери». */
function isTrunkMode(m) {
    return m === 'RAIL' || m === 'SEA' || m === 'INLAND_WATER' || m === 'AIR';
}
/**
 * Типовая раскладка маршрута по плечам для набора видов транспорта.
 *
 * «жд + авто» → авто (отправитель → станция), жд (станция → станция),
 * авто (станция → получатель). Наименования узлов пустые — их заполняет
 * пользователь; концы подставляются из точек погрузки/выгрузки поручения.
 */
export function buildDefaultRouteSegments(modes, ends = {}) {
    const clean = modes.filter((m) => m !== 'ANY' && m !== 'MULTIMODAL');
    const trunk = clean.filter(isTrunkMode);
    const hasAuto = clean.includes('AUTO');
    /** Порядок плеч: подвоз авто → магистраль(и) → развоз авто. */
    const chain = trunk.length
        ? [...(hasAuto ? ['AUTO'] : []), ...trunk, ...(hasAuto ? ['AUTO'] : [])]
        : clean.length
            ? [clean[0]]
            : ['AUTO'];
    return chain.map((mode, i) => {
        const first = i === 0;
        const last = i === chain.length - 1;
        // Тип узла на стыке: берём «более узловой» из двух соседних видов.
        const joinKind = (a, b) => MODE_POINT_KIND[isTrunkMode(a) ? a : b] ?? 'TERMINAL';
        return {
            sequence: i + 1,
            transportMode: mode,
            fromKind: first ? 'SHIPPER' : joinKind(chain[i - 1], mode),
            fromName: first ? (ends.pickupName ?? '') : '',
            fromCountry: first ? (ends.pickupCountry ?? undefined) : undefined,
            toKind: last ? 'CONSIGNEE' : joinKind(chain[i + 1], mode),
            toName: last ? (ends.dropoffName ?? '') : '',
            toCountry: last ? (ends.dropoffCountry ?? undefined) : undefined,
        };
    });
}
/**
 * Проверка плеч: стыковка и заполненность узлов. Возвращает список замечаний —
 * они предупреждают, но не блокируют сохранение черновика.
 */
export function validateRouteSegments(segments) {
    const problems = [];
    if (segments.length === 0)
        return problems;
    segments.forEach((s, i) => {
        const n = i + 1;
        if (!s.fromName.trim())
            problems.push(`плечо ${n}: не указан пункт отправления`);
        if (!s.toName.trim())
            problems.push(`плечо ${n}: не указан пункт назначения`);
        const next = segments[i + 1];
        if (next && s.toName.trim() && next.fromName.trim() && s.toName.trim() !== next.fromName.trim()) {
            problems.push(`плечи ${n} и ${n + 1} не стыкуются: «${s.toName}» ≠ «${next.fromName}»`);
        }
    });
    return problems;
}
/** Плечо целиком по России — по коду страны или названию (RU / «Россия»). */
export function isDomesticRuSegment(s) {
    const ru = (code, name) => {
        if (code)
            return code.trim().toUpperCase() === 'RU';
        const n = (name ?? '').trim().toLowerCase();
        return n === 'россия' || n === 'рф' || n === 'russia';
    };
    return ru(s.fromCountryCode, s.fromCountry) && ru(s.toCountryCode, s.toCountry);
}
// =====================================================================
// OrderExtraService
// =====================================================================
export const orderExtraServiceInputSchema = z.object({
    /** Код услуги из справочника (ServiceCatalogItem.code). */
    code: z.string().max(100),
    /** Снимок названия услуги (русское) на момент поручения. */
    name: z.string().max(200).optional(),
    details: z.string().max(2000).optional(),
});
export const orderExtraServiceDtoSchema = orderExtraServiceInputSchema.extend({
    id: z.string(),
    orderId: z.string(),
    name: z.string().nullable(),
});
// =====================================================================
// Order CREATE
// =====================================================================
/**
 * Создание/правка поручения.
 *
 * Инварианты MVP:
 * - В массиве routePoints обязательно ровно одна PICKUP и ровно одна DROPOFF
 *   (валидируется через superRefine). Архитектурно поддерживается мультидроп —
 *   ограничение снимается переключателем в этапе 5+.
 * - cargoIsDangerous=true ⇒ cargoAdrClass обязательно.
 */
/** Валюты стоимости груза. */
export const cargoCurrencySchema = z.enum(['RUB', 'USD', 'EUR', 'CNY', 'KZT', 'BYN']);
/**
 * Код валюты (ISO 4217 alpha-3) — из классификатора валют ФТС. Ослаблен до
 * произвольного 3-буквенного кода (раньше был строгий enum из 6 валют).
 */
export const currencyCodeSchema = z.string().trim().min(1).max(3);
/** Условия поставки (Incoterms 2020). */
export const deliveryTermsSchema = z.enum([
    'EXW', 'FCA', 'CPT', 'CIP', 'DAP', 'DPU', 'DDP',
    'FAS', 'FOB', 'CFR', 'CIF',
]);
/** Тип контейнера. */
export const containerTypeSchema = z.enum(['20', '40', '40HC', '45']);
/** Один контейнер в списке: тип + номер (count — legacy для старых поручений). */
export const containerSpecSchema = z.object({
    type: containerTypeSchema,
    number: z.string().max(40).optional(),
    count: z.number().int().positive().optional(),
});
/** Вид единицы транспорта. */
export const transportUnitKindSchema = z.enum(['CONTAINER', 'WAGON']);
/**
 * Принадлежность единицы транспорта.
 * Контейнер: SOC — собственность клиента (грузоотправителя), COC — перевозчика (линии).
 * Вагон: OWNED — собственный, INVENTORY — инвентарный (парк перевозчика/РЖД).
 */
export const transportUnitOwnershipSchema = z.enum(['SOC', 'COC', 'OWNED', 'INVENTORY']);
/**
 * Единица транспорта — отдельный контейнер (или вагон, фаза 2).
 *
 * `id` стабилен и присутствует у уже сохранённых единиц; у новых единиц,
 * созданных на клиенте, может отсутствовать (сервер выдаст). `number`
 * опционален — вписывается по мере поступления, в т.ч. на этапе перевозки.
 */
export const transportUnitSchema = z.object({
    id: z.string().optional(),
    kind: transportUnitKindSchema.default('CONTAINER'),
    type: z.string().max(40),
    number: z.string().max(40).optional().nullable(),
    /** Принадлежность (контейнер SOC/COC, вагон OWNED/INVENTORY). null/undefined — не указана. */
    ownership: transportUnitOwnershipSchema.optional().nullable(),
    seq: z.number().int().nonnegative().optional().nullable(),
});
/**
 * Развернуть позицию «тип × количество» в массив единиц без номеров.
 * Используется UI при вводе количеством. `startSeq` — с какого порядкового
 * номера начинать (чтобы seq были сквозными внутри типа).
 */
export function expandPositionToUnits(type, count, kind = 'CONTAINER', startSeq = 1, ownership = null) {
    return Array.from({ length: Math.max(0, count) }, (_, i) => ({
        kind,
        type,
        number: null,
        ownership,
        seq: startSeq + i,
    }));
}
/** Группа груза по единицам транспорта (если в контейнерах разный груз). */
export const cargoGroupSchema = z.object({
    /**
     * id единиц транспорта (TransportUnit.id), к которым относится этот груз.
     * Распределение по стабильным id, а не по номерам, — работает и без номеров.
     */
    unitIds: z.array(z.string()).default([]),
    /** @deprecated legacy: номера контейнеров. Оставлено для старых поручений. */
    containers: z.array(z.string()).optional(),
    cargoName: z.string().max(200),
    cargoWeightKg: z.number().nonnegative().optional(),
    cargoWeightNetKg: z.number().nonnegative().optional(),
    cargoVolumeM3: z.number().nonnegative().optional(),
    cargoUnits: z.number().int().nonnegative().optional(),
    cargoPackaging: z.string().max(200).optional(),
    cargoOriginCountry: z.string().max(100).optional(),
    cargoTnVed: z.string().max(20).optional(),
    cargoMarking: z.string().max(1000).optional(),
    cargoIsDangerous: z.boolean().optional(),
    cargoAdrClass: z.string().max(20).optional(),
});
export const orderCreateSchema = z
    .object({
    transportMode: transportModeSchema,
    /** Виды транспорта (мультимодальная — несколько). */
    transportModes: z.array(transportModeSchema).default([]),
    direction: orderDirectionSchema,
    /** Тип перевозки: FTL, LTL, контейнер 20', 40' и т.п. */
    shipmentType: z.string().max(100).optional(),
    /** Коды видов транспорта по классификатору ФТС (для транзитной декларации). */
    transportTypeCodes: z.array(z.string().max(10)).max(20).optional(),
    /** Рамочный договор ТЭО, на основании которого выдано поручение. */
    agreementId: z.string().optional(),
    /** Плательщик — сам клиент (по умолчанию). Иначе заполняются payer*-поля. */
    payerIsClient: z.boolean().default(true),
    payerName: z.string().max(500).optional(),
    payerType: counterpartyPartyTypeSchema.optional(),
    payerInn: z.string().max(20).optional(),
    payerKpp: z.string().max(20).optional(),
    payerOgrn: z.string().max(20).optional(),
    payerAddress: z.string().max(500).optional(),
    cargoName: z.string().max(200),
    /** Категория груза — опц., скрыта в форме на текущем этапе. */
    cargoCategory: z.string().max(100).optional(),
    cargoIsDangerous: z.boolean().default(false),
    cargoAdrClass: z.string().max(20).optional(),
    /** ООН-номер опасного груза (1–4 цифры). */
    cargoUnNumber: z.string().regex(/^\d{1,4}$/, 'ООН — 1–4 цифры').optional(),
    cargoTnVed: z.string().max(20).optional(),
    /** ЕТ СНГ — Единая тарифная статистическая номенклатура СНГ. */
    cargoEtSng: z.string().max(20).optional(),
    /** ГНГ — Гармонизированная номенклатура грузов. */
    cargoGng: z.string().max(20).optional(),
    /** Декларируемая стоимость с налогом (итоговая). */
    cargoValue: z.number().nonnegative().optional(),
    /** Стоимость без налога. */
    cargoValueNet: z.number().nonnegative().optional(),
    /** Ставка НДС в процентах (0..100). */
    cargoVatRate: z.number().min(-1).max(100).optional(),
    /** Валюта стоимости. По умолчанию RUB. */
    cargoValueCurrency: currencyCodeSchema.default('RUB'),
    /** Вес брутто (с упаковкой), кг. */
    cargoWeightKg: z.number().nonnegative(),
    /** Вес нетто, кг. */
    cargoWeightNetKg: z.number().nonnegative().optional(),
    cargoVolumeM3: z.number().nonnegative().optional(),
    cargoUnits: z.number().int().nonnegative().optional(),
    cargoPackaging: z.string().max(200).optional(),
    cargoPackagingCode: z.string().max(40).optional(),
    temperatureMode: z.string().max(100).optional(),
    // --- Реквизиты формата ФНС ЕД-1-26/277@ (титул 1). См. compliance-poruchenie-277.md. ---
    /** Маркировка груза (ОпГруз/Марк). */
    cargoMarking: z.string().max(1000).optional(),
    /** Страна происхождения груза (ОпГруз/СвСтрПроисх). */
    cargoOriginCountry: z.string().max(100).optional(),
    /** Код страны происхождения (классификатор ФТС). */
    cargoOriginCountryCode: z.string().max(10).optional(),
    /** Код по ОКПД 2 (ОпГруз/КодТовОКПД2). */
    cargoOkpd2: z.string().max(20).optional(),
    /** Вес тары, кг (ВесГруз/@ВесТар). */
    cargoTareWeightKg: z.number().nonnegative().optional(),
    /** Габариты грузового места, метры (ИнфУпГруз/ГабарУпак). */
    packageLengthM: z.number().nonnegative().optional(),
    packageWidthM: z.number().nonnegative().optional(),
    packageHeightM: z.number().nonnegative().optional(),
    /** Признак уведомления сторон о прибытии груза (СвГруз/@ПрУведСторон). */
    notifyOnArrival: z.boolean().default(false),
    /** Кого уведомить о прибытии (СвУведСторон). */
    notifyParties: z.array(notifyPartySchema).default([]),
    /** Приём груза во владение экспедитором (СвГруз/@ОтмПрием). */
    expeditorTakesPossession: z.boolean().optional(),
    /** Особые отметки (УказКлнт/@Отмет). */
    specialMarks: z.string().max(2000).optional(),
    /** Климат — пределы температуры, °C (УказКлнт/КлимРеж). */
    tempMinC: z.number().optional(),
    tempMaxC: z.number().optional(),
    /** @deprecated Список контейнеров. Заменён на transportUnits. */
    containers: z.array(containerSpecSchema).optional(),
    /** Единицы транспорта (контейнеры/вагоны). */
    transportUnits: z.array(transportUnitSchema).optional(),
    /** Один груз во всех контейнерах. */
    sameCargoForAll: z.boolean().default(true),
    /** Груз по контейнерам/группам (если sameCargoForAll=false). */
    cargoGroups: z.array(cargoGroupSchema).optional(),
    /** Желаемая дата начала перевозки (ISO). */
    preferredDateFrom: z.string().datetime().optional(),
    /** Желаемая дата окончания перевозки (ISO). */
    preferredDateTo: z.string().datetime().optional(),
    /** Условия поставки по контракту (Incoterms 2020). */
    deliveryTerms: deliveryTermsSchema.optional(),
    /** Номер контейнера (заполняется при CIF). */
    containerNumber: z.string().max(40).optional(),
    /** Номер коносамента (заполняется при CIF). */
    billOfLadingNumber: z.string().max(40).optional(),
    comment: z.string().max(5000).optional(),
    routePoints: z.array(routePointInputSchema),
    /** Маршрут уточнён по плечам. */
    routeDetailed: z.boolean().default(false),
    /** Плечи маршрута (опц.) — детализация при нескольких видах транспорта. */
    routeSegments: z.array(orderRouteSegmentInputSchema).max(20).optional(),
    extraServices: z.array(orderExtraServiceInputSchema).default([]),
});
export function validateOrderCompleteness(o) {
    const problems = [];
    if (!o.agreementId)
        problems.push('не выбран договор ТЭО');
    if (!o.cargoName || !o.cargoName.trim())
        problems.push('не указано наименование груза');
    if (o.cargoWeightKg == null || o.cargoWeightKg <= 0)
        problems.push('не указан вес брутто');
    const pickups = o.routePoints.filter((p) => p.kind === 'PICKUP');
    const dropoffs = o.routePoints.filter((p) => p.kind === 'DROPOFF');
    if (pickups.length !== 1 || dropoffs.length !== 1) {
        problems.push('нужна ровно одна точка погрузки и одна выгрузки');
    }
    if (pickups[0] && !pickups[0].address?.trim())
        problems.push('не указан адрес погрузки');
    if (dropoffs[0] && !dropoffs[0].address?.trim())
        problems.push('не указан адрес выгрузки');
    if (o.cargoIsDangerous && !o.cargoAdrClass?.trim()) {
        problems.push('для опасного груза обязателен класс ДОПОГ');
    }
    if (o.deliveryTerms === 'CIF') {
        if (!o.containerNumber?.trim())
            problems.push('для CIF обязателен номер контейнера');
        if (!o.billOfLadingNumber?.trim())
            problems.push('для CIF обязателен номер коносамента');
    }
    return problems;
}
// =====================================================================
// PATCH (только DRAFT) — все поля опциональны
// =====================================================================
export const orderPatchSchema = z
    .object({
    transportMode: transportModeSchema.optional(),
    transportModes: z.array(transportModeSchema).optional(),
    transportTypeCodes: z.array(z.string().max(10)).max(20).optional(),
    direction: orderDirectionSchema.optional(),
    shipmentType: z.string().max(100).optional(),
    agreementId: z.string().nullable().optional(),
    payerIsClient: z.boolean().optional(),
    payerName: z.string().max(500).optional(),
    payerType: counterpartyPartyTypeSchema.optional(),
    payerInn: z.string().max(20).optional(),
    payerKpp: z.string().max(20).optional(),
    payerOgrn: z.string().max(20).optional(),
    payerAddress: z.string().max(500).optional(),
    cargoName: z.string().max(200).optional(),
    cargoCategory: z.string().min(1).max(100).optional(),
    cargoIsDangerous: z.boolean().optional(),
    cargoAdrClass: z.string().max(20).optional(),
    cargoUnNumber: z.string().regex(/^\d{1,4}$/).optional(),
    cargoTnVed: z.string().max(20).optional(),
    cargoEtSng: z.string().max(20).optional(),
    cargoGng: z.string().max(20).optional(),
    cargoValue: z.number().nonnegative().optional(),
    cargoValueNet: z.number().nonnegative().optional(),
    cargoVatRate: z.number().min(-1).max(100).optional(),
    cargoValueCurrency: currencyCodeSchema.optional(),
    cargoWeightKg: z.number().nonnegative().optional(),
    cargoWeightNetKg: z.number().nonnegative().optional(),
    cargoVolumeM3: z.number().nonnegative().optional(),
    cargoUnits: z.number().int().nonnegative().optional(),
    cargoPackaging: z.string().max(200).optional(),
    cargoPackagingCode: z.string().max(40).optional(),
    temperatureMode: z.string().max(100).optional(),
    cargoMarking: z.string().max(1000).optional(),
    cargoOriginCountry: z.string().max(100).optional(),
    cargoOriginCountryCode: z.string().max(10).optional(),
    cargoOkpd2: z.string().max(20).optional(),
    cargoTareWeightKg: z.number().nonnegative().optional(),
    packageLengthM: z.number().nonnegative().optional(),
    packageWidthM: z.number().nonnegative().optional(),
    packageHeightM: z.number().nonnegative().optional(),
    notifyOnArrival: z.boolean().optional(),
    notifyParties: z.array(notifyPartySchema).optional(),
    expeditorTakesPossession: z.boolean().optional(),
    specialMarks: z.string().max(2000).optional(),
    tempMinC: z.number().optional(),
    tempMaxC: z.number().optional(),
    containers: z.array(containerSpecSchema).optional(),
    transportUnits: z.array(transportUnitSchema).optional(),
    sameCargoForAll: z.boolean().optional(),
    cargoGroups: z.array(cargoGroupSchema).optional(),
    preferredDateFrom: z.string().datetime().optional(),
    preferredDateTo: z.string().datetime().optional(),
    deliveryTerms: deliveryTermsSchema.optional(),
    containerNumber: z.string().max(40).optional(),
    billOfLadingNumber: z.string().max(40).optional(),
    comment: z.string().max(5000).optional(),
    routePoints: z.array(routePointInputSchema).optional(),
    routeDetailed: z.boolean().optional(),
    routeSegments: z.array(orderRouteSegmentInputSchema).max(20).optional(),
    extraServices: z.array(orderExtraServiceInputSchema).optional(),
});
/**
 * Правка плеч маршрута экспедитором — отдельно от общего патча поручения,
 * потому что доступна и после подачи (маршрут уточняется по ходу работы).
 */
export const orderRouteSegmentsPatchSchema = z.object({
    routeDetailed: z.boolean().optional(),
    routeSegments: z.array(orderRouteSegmentInputSchema).max(20),
});
/**
 * Патч единиц транспорта на этапе перевозки (staff).
 * Полная замена набора единиц поручения + опционально — распределения груза.
 */
export const shipmentUnitsPatchSchema = z.object({
    transportUnits: z.array(transportUnitSchema),
    sameCargoForAll: z.boolean().optional(),
    cargoGroups: z.array(cargoGroupSchema).optional(),
});
// =====================================================================
// Attachment
// =====================================================================
/** Категория вложения — определяет смысл файла в UI. */
export const attachmentCategorySchema = z.enum([
    'GENERAL',
    'MSDS_RU',
    'MSDS_EN',
    'WAYBILL',
    'INVOICE',
    'PHOTO',
]);
export const attachmentDtoSchema = z.object({
    id: z.string(),
    orderId: z.string().nullable(),
    shipmentId: z.string().nullable(),
    cargoEventId: z.string().nullable(),
    fileName: z.string(),
    mimeType: z.string(),
    sizeBytes: z.number().int().nonnegative(),
    uploadedByUserId: z.string(),
    category: z.string().nullable(),
    createdAt: z.string().datetime(),
});
// =====================================================================
// Read DTO
// =====================================================================
export const orderListItemSchema = z.object({
    id: z.string(),
    number: z.string(),
    status: orderStatusSchema,
    transportMode: transportModeSchema,
    direction: orderDirectionSchema,
    cargoName: z.string(),
    cargoWeightKg: z.number(),
    createdAt: z.string().datetime(),
    submittedAt: z.string().datetime().nullable(),
    clientId: z.string(),
    /** Доступно только в manager-view. */
    clientDisplayName: z.string().nullable().optional(),
});
/** Карточка поручения — фронт собирает её через GET /orders/:id. */
export const orderDetailSchema = orderListItemSchema.extend({
    transportModes: z.array(transportModeSchema),
    transportTypeCodes: z.array(z.string()),
    /** Перевозка по этому поручению (проект/рабочая), если создана — фаза 4. */
    shipmentId: z.string().nullable(),
    shipmentStatus: z.string().nullable(),
    shipmentType: z.string().nullable(),
    agreementId: z.string().nullable(),
    agreementNumber: z.string().nullable(),
    agreementSignedDate: z.string().nullable(),
    payerIsClient: z.boolean(),
    payerName: z.string().nullable(),
    payerType: z.string().nullable(),
    payerInn: z.string().nullable(),
    payerKpp: z.string().nullable(),
    payerOgrn: z.string().nullable(),
    payerAddress: z.string().nullable(),
    cargoCategory: z.string().nullable(),
    cargoIsDangerous: z.boolean(),
    cargoAdrClass: z.string().nullable(),
    cargoUnNumber: z.string().nullable(),
    cargoTnVed: z.string().nullable(),
    cargoEtSng: z.string().nullable(),
    cargoGng: z.string().nullable(),
    cargoValue: z.number().nullable(),
    cargoValueNet: z.number().nullable(),
    cargoVatRate: z.number().nullable(),
    cargoValueCurrency: z.string().nullable(),
    cargoWeightNetKg: z.number().nullable(),
    cargoVolumeM3: z.number().nullable(),
    cargoUnits: z.number().int().nullable(),
    cargoPackaging: z.string().nullable(),
    cargoPackagingCode: z.string().nullable(),
    temperatureMode: z.string().nullable(),
    // Реквизиты формата ФНС ЕД-1-26/277@ (титул 1).
    cargoMarking: z.string().nullable(),
    cargoOriginCountry: z.string().nullable(),
    cargoOriginCountryCode: z.string().nullable(),
    cargoOkpd2: z.string().nullable(),
    cargoTareWeightKg: z.number().nullable(),
    packageLengthM: z.number().nullable(),
    packageWidthM: z.number().nullable(),
    packageHeightM: z.number().nullable(),
    notifyOnArrival: z.boolean(),
    notifyParties: z.array(notifyPartySchema),
    expeditorTakesPossession: z.boolean().nullable(),
    specialMarks: z.string().nullable(),
    tempMinC: z.number().nullable(),
    tempMaxC: z.number().nullable(),
    containers: z.array(containerSpecSchema).nullable(),
    transportUnits: z.array(transportUnitSchema),
    sameCargoForAll: z.boolean(),
    cargoGroups: z.array(cargoGroupSchema).nullable(),
    preferredDateFrom: z.string().datetime().nullable(),
    preferredDateTo: z.string().datetime().nullable(),
    deliveryTerms: z.string().nullable(),
    containerNumber: z.string().nullable(),
    billOfLadingNumber: z.string().nullable(),
    comment: z.string().nullable(),
    updatedAt: z.string().datetime(),
    routePoints: z.array(routePointDtoSchema),
    routeDetailed: z.boolean(),
    routeSegments: z.array(orderRouteSegmentDtoSchema),
    extraServices: z.array(orderExtraServiceDtoSchema),
    attachments: z.array(attachmentDtoSchema),
});
// =====================================================================
// List query
// =====================================================================
export const orderListQuerySchema = z.object({
    status: orderStatusSchema.optional(),
    clientId: z.string().optional(),
    fromDate: z.string().datetime().optional(),
    toDate: z.string().datetime().optional(),
});
//# sourceMappingURL=orders.js.map